import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Input, Label } from '@alertwhale/ui'
import {
  useAuthConfig,
  useRequestOTP,
  useVerifyOTP,
  useVerify2FA,
  useVerifyBackupCode,
  useGoogleOAuthCallback,
  useGitHubOAuthCallback,
} from '@/hooks/useAuth'
import { useAuthStore } from '@/stores/authStore'
import { Mail, Loader2, KeyRound, Shield } from 'lucide-react'

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string
          callback: (token: string) => void
          'expired-callback'?: () => void
          theme?: 'dark' | 'light'
        }
      ) => number
      reset: (widgetId?: number) => void
    }
  }
}

type AuthStep = 'credentials' | 'otp-verify' | '2fa'

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: authConfig, isLoading: configLoading } = useAuthConfig()

  // Auth state
  const [step, setStep] = useState<AuthStep>('credentials')
  const [email, setEmail] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [twoFACode, setTwoFACode] = useState('')
  const [isNewUser, setIsNewUser] = useState(false)
  const [name, setName] = useState('')
  const [useBackupCode, setUseBackupCode] = useState(false)

  // reCAPTCHA
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const recaptchaRef = useRef<HTMLDivElement>(null)
  const recaptchaWidgetId = useRef<number | null>(null)

  // Mutations
  const requestOTP = useRequestOTP()
  const verifyOTP = useVerifyOTP()
  const verify2FA = useVerify2FA()
  const verifyBackupCode = useVerifyBackupCode()
  const googleCallback = useGoogleOAuthCallback()
  const githubCallback = useGitHubOAuthCallback()

  // Store state
  const { requires2FA, tempToken } = useAuthStore()

  // Handle OAuth callbacks
  useEffect(() => {
    const code = searchParams.get('code')
    const state = searchParams.get('state')

    if (code && state === 'google-oauth') {
      googleCallback.mutate({ code })
    } else if (code && state === 'github-oauth') {
      githubCallback.mutate({ code })
    }
  }, [searchParams])

  // Handle 2FA requirement from any login method
  useEffect(() => {
    if (requires2FA && tempToken) {
      setStep('2fa')
    }
  }, [requires2FA, tempToken])

  // Initialize reCAPTCHA v2
  useEffect(() => {
    if (!authConfig?.recaptcha?.enabled || !authConfig?.recaptcha?.siteKey) return
    if (step !== 'credentials') return

    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit'
    script.async = true
    script.defer = true

    script.onload = () => {
      if (window.grecaptcha && recaptchaRef.current) {
        window.grecaptcha.ready(() => {
          if (recaptchaRef.current && recaptchaWidgetId.current === null) {
            recaptchaWidgetId.current = window.grecaptcha.render(recaptchaRef.current, {
              sitekey: authConfig.recaptcha.siteKey!,
              callback: (token: string) => setRecaptchaToken(token),
              'expired-callback': () => setRecaptchaToken(null),
              theme: 'dark',
            })
          }
        })
      }
    }

    document.head.appendChild(script)

    return () => {
      const existingScript = document.querySelector('script[src*="recaptcha"]')
      if (existingScript) {
        existingScript.remove()
      }
    }
  }, [authConfig?.recaptcha?.enabled, authConfig?.recaptcha?.siteKey, step])

  const resetRecaptcha = useCallback(() => {
    if (window.grecaptcha && recaptchaWidgetId.current !== null) {
      window.grecaptcha.reset(recaptchaWidgetId.current)
      setRecaptchaToken(null)
    }
  }, [])

  // Handle OTP request
  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault()

    if (authConfig?.recaptcha?.enabled && !recaptchaToken) {
      return
    }

    try {
      const result = await requestOTP.mutateAsync({
        email,
        recaptchaToken: recaptchaToken || undefined,
      })
      setIsNewUser(result.isNewUser)
      setStep('otp-verify')
    } catch {
      resetRecaptcha()
    }
  }

  // Handle OTP verification
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      await verifyOTP.mutateAsync({
        email,
        code: otpCode,
        name: isNewUser ? name : undefined,
      })
      if (!requires2FA) {
        navigate('/')
      }
    } catch {
      setOtpCode('')
    }
  }

  // Handle 2FA verification
  const handle2FA = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!tempToken) return

    try {
      if (useBackupCode) {
        await verifyBackupCode.mutateAsync({
          token: tempToken,
          code: twoFACode,
        })
      } else {
        await verify2FA.mutateAsync({
          token: tempToken,
          code: twoFACode,
        })
      }
      navigate('/')
    } catch {
      setTwoFACode('')
    }
  }

  // Handle Google OAuth
  const handleGoogleAuth = () => {
    const redirectUri = `${window.location.origin}/login`
    window.location.href = `/api/auth/google?redirect_uri=${encodeURIComponent(redirectUri)}`
  }

  // Handle GitHub OAuth
  const handleGitHubAuth = () => {
    const redirectUri = `${window.location.origin}/login`
    window.location.href = `/api/auth/github?redirect_uri=${encodeURIComponent(redirectUri)}`
  }

  const isLoading =
    requestOTP.isPending ||
    verifyOTP.isPending ||
    verify2FA.isPending ||
    verifyBackupCode.isPending ||
    googleCallback.isPending ||
    githubCallback.isPending

  const error =
    requestOTP.error?.message ||
    verifyOTP.error?.message ||
    verify2FA.error?.message ||
    verifyBackupCode.error?.message ||
    googleCallback.error?.message ||
    githubCallback.error?.message

  if (configLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-whale-400" />
        </div>
      </div>
    )
  }

  // 2FA Step
  if (step === '2fa') {
    return (
      <div className="p-8">
        <div className="space-y-1 mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="h-6 w-6 text-whale-400" />
            Two-Factor Authentication
          </h2>
          <p className="text-slate-400">
            {useBackupCode
              ? 'Enter one of your backup codes'
              : 'Enter the 6-digit code from your authenticator app'}
          </p>
        </div>
        <form onSubmit={handle2FA} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="2fa-code" className="text-slate-300">
              {useBackupCode ? 'Backup Code' : 'Authentication Code'}
            </Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                id="2fa-code"
                type="text"
                placeholder={useBackupCode ? 'XXXXXXXX' : '000000'}
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.toUpperCase())}
                className="pl-10 text-center tracking-widest bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-whale-500 focus:ring-whale-500"
                maxLength={useBackupCode ? 8 : 6}
                required
                autoFocus
              />
            </div>
          </div>

          {error && <div className="text-sm text-red-400">{error}</div>}

          <Button
            type="submit"
            className="w-full bg-whale-500 hover:bg-whale-600 text-white"
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Verify
          </Button>

          <div className="text-center">
            <button
              type="button"
              className="text-sm text-whale-400 hover:text-whale-300"
              onClick={() => {
                setUseBackupCode(!useBackupCode)
                setTwoFACode('')
              }}
            >
              {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
            </button>
          </div>
        </form>
      </div>
    )
  }

  // OTP Verification Step
  if (step === 'otp-verify') {
    return (
      <div className="p-8">
        <div className="space-y-1 mb-6">
          <h2 className="text-2xl font-bold text-white">Verify Your Email</h2>
          <p className="text-slate-400">
            We sent a 6-digit code to <strong className="text-white">{email}</strong>
          </p>
        </div>
        <form onSubmit={handleVerifyOTP} className="space-y-4">
          {isNewUser && (
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-300">
                Your Name
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-whale-500 focus:ring-whale-500"
                required
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="otp" className="text-slate-300">
              Verification Code
            </Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                id="otp"
                type="text"
                placeholder="000000"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                className="pl-10 text-center tracking-widest bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-whale-500 focus:ring-whale-500"
                maxLength={6}
                required
                autoFocus
              />
            </div>
          </div>

          {error && <div className="text-sm text-red-400">{error}</div>}

          <Button
            type="submit"
            className="w-full bg-whale-500 hover:bg-whale-600 text-white"
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isNewUser ? 'Create Account' : 'Sign In'}
          </Button>

          <div className="text-center">
            <button
              type="button"
              className="text-sm text-slate-400 hover:text-white"
              onClick={() => {
                setStep('credentials')
                setOtpCode('')
              }}
            >
              Back to login
            </button>
          </div>
        </form>
      </div>
    )
  }

  // Main Login Step - Email OTP only
  return (
    <div className="p-8">
      <div className="space-y-1 mb-6">
        <h2 className="text-2xl font-bold text-white">Sign in</h2>
        <p className="text-slate-400">Enter your email to receive a sign-in code</p>
      </div>
      <form onSubmit={handleRequestOTP} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-300">
            Email
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-whale-500 focus:ring-whale-500"
              required
            />
          </div>
        </div>

        <p className="text-sm text-slate-500">
          We'll send a one-time code to your email. No password needed.
        </p>

        {authConfig?.recaptcha?.enabled && (
          <div className="flex justify-center">
            <div ref={recaptchaRef} />
          </div>
        )}

        {error && <div className="text-sm text-red-400">{error}</div>}

        <Button
          type="submit"
          className="w-full bg-whale-500 hover:bg-whale-600 text-white font-semibold"
          disabled={isLoading || (authConfig?.recaptcha?.enabled && !recaptchaToken)}
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Send Code
        </Button>
      </form>

      {(authConfig?.googleOAuth?.enabled || authConfig?.githubOAuth?.enabled) && (
        <>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-700" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-transparent px-2 text-slate-500">Or continue with</span>
            </div>
          </div>

          <div className="grid gap-3">
            {authConfig?.googleOAuth?.enabled && (
              <Button
                variant="outline"
                type="button"
                className="w-full h-11 bg-slate-900/50 border-slate-700 text-white hover:bg-slate-800 hover:text-white"
                onClick={handleGoogleAuth}
                disabled={isLoading}
              >
                <svg className="h-5 w-5 shrink-0 mr-2" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                <span className="font-medium">Continue with Google</span>
              </Button>
            )}

            {authConfig?.githubOAuth?.enabled && (
              <Button
                variant="outline"
                type="button"
                className="w-full h-11 bg-slate-900/50 border-slate-700 text-white hover:bg-slate-800 hover:text-white"
                onClick={handleGitHubAuth}
                disabled={isLoading}
              >
                <svg className="h-5 w-5 shrink-0 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                <span className="font-medium">Continue with GitHub</span>
              </Button>
            )}
          </div>
        </>
      )}

      <p className="text-center text-sm text-slate-500 mt-6">
        Don't have an account? Just enter your email above and we'll create one for you.
      </p>
    </div>
  )
}
